/* ===== sprites.js — 손그림 애셋 로더(매니페스트 · 발 여백) — 없어도 절차 생성 그림으로 돈다 ===== */
import { ASSET_VER, imageJob, measurePad } from '../engine/assets/image.js';
export const Sprites: Bag = {
  base: 'assets/',
  scale: 4,           // 시트가 4배로 구워져 있다
  gap: 0,             // 문자 시트는 간격 0, 보스/이펙트 시트는 4
  img: {}, meta: null, loaded: 0, total: 0,
  lazy: {} as Record<string, string>, loading: {} as Record<string, 1>, failed: {} as Record<string, 1>, used: {} as Record<string, number>, baseOf: {} as Record<string, string>,
  _ver: ASSET_VER,
  /** CSS·DOM 에서 그림을 부를 때도 같은 ?v= 를 붙인다 — 배포는 /play/assets/*.png 를 1년 immutable 로 캐시해서,
      ?v= 없는 주소는 시트를 다시 구워도 옛 그림이 나온다(옛 32칸 시트가 36칸 자리로 늘어나 뚱뚱해 보였다). */
  url(path: string) { return path + (this._ver ? '?' + this._ver : ''); },

  async ready() {
    /* 매니페스트는 <script> 로 미리 들어와 있다(assets/sprites-manifest.js) — 사연: docs/code-history.md#h137 */
    this.meta = (typeof window !== 'undefined' && window.SPRITE_MANIFEST) || null;
    if (!this.meta) {
      this.meta = await (await fetch(this.base + 'manifest.json', { cache: 'no-cache' })).json();
    }
    const jobs: Promise<any>[] = [];
    const add = (key: string, file: any) => jobs.push(imageJob(this.base + file + (this._ver ? '?' + this._ver : ''), im => { this.img[key] = im; }));
    const C = this.meta.characters.sheets, B = this.meta.bosses.sheets;
    for (const k in C) add(k, C[k].file);
    /* 보스 시트는 처음에 안 읽는다 — 펴면 338 MB(그림 전체 570 MB 중)인데 한 번에 한 보스만 싸운다. 깨울 때 want() · 2분 안 그리면 gc().
       사연: docs/code-history.md#h206 */
    for (const k in B) this.lazy[k] = B[k].file;
    for (const k in this.meta.fx.projectiles.files) add('proj_' + k, this.meta.fx.projectiles.files[k]);
    for (const k in this.meta.fx.bursts.files) add('burst_' + k, this.meta.fx.bursts.files[k]);
    // 연기(용광로 굴뚝) — 투사체·폭발과 규격만 다른 세 번째 이펙트 무리
    if (this.meta.fx.smoke) for (const k in this.meta.fx.smoke.files) add('smoke_' + k, this.meta.fx.smoke.files[k]);
    if (this.meta.fx.vfx) for (const k in this.meta.fx.vfx.files) add('vfx_' + k, this.meta.fx.vfx.files[k]);   // 스킬 연출 결(tools/mkvfx.py)
    for (const k in this.meta.npc.files) add('npc_' + k, this.meta.npc.files[k]);
    this.meta.backgrounds.parallax.files.forEach((f: any) => add(f.split('/')[1].replace('.png',''), f));
    add('title', this.meta.backgrounds.title.file);
    /* 장 그림(chapter_*)은 여기서 안 읽는다 — 대화창이 CSS 주소로 그때 부른다(ui/dialogue.ts). 미리 펴 두면 18장 63 MB 가 쓰이지 않은 채 남았다 */
    // 구름 — 절이 있을 때만 읽는다(옛 매니페스트에는 없다)
    if (this.meta.backgrounds.clouds)
      this.meta.backgrounds.clouds.files.forEach((f: any) => add(f.split('/')[1].replace('.png', ''), f));
    // 하늘의 해·운석(tools/mksky.py) — 없으면 게임이 절차 그림으로 떨어진다
    if (this.meta.backgrounds.sky)
      this.meta.backgrounds.sky.files.forEach((f: any) => add(f.split('/')[1].replace('.png', ''), f));
    // 여명 마을 시설물 (한 장짜리 정지 이미지, 상태별 변형 파일이 있을 수 있다)
    if (this.meta.objects) for (const k in this.meta.objects.files) {
      const o = this.meta.objects.files[k];
      add('obj_' + k, o.file);
      if (o.off) add('obj_' + k + '_off', o.off);
      if (o.read) add('obj_' + k + '_read', o.read);
    }
    // 손그림 타일 텍스처 (22×22, 절차 생성 아틀라스를 대체)
    if (this.meta.tiles) for (const k in this.meta.tiles.files) add('tile_' + k, this.meta.tiles.files[k]);
    // 손그림 아이템 아이콘 (32×32, 절차 생성 아이콘 아틀라스를 대체)
    if (this.meta.items) for (const k in this.meta.items.files) add('item_' + k, this.meta.items.files[k]);
    await Promise.all(jobs);
    /* 프레임 0의 알파 채널을 실측해 진짜 여백을 한 번만 재고 캐시해 둔다. */
    /* ★ file:// 에서는 이 측정이 통째로 막힌다 — 사연: docs/code-history.md#h138 */
    this.footInset = {};
    this.sideInset = {};   // 시트별 그림의 가로 치우침(게임픽셀)
    for (const k in { ...this.meta.characters.sheets, ...this.meta.bosses.sheets }) {
      const m = this.meta.characters.sheets[k] || this.meta.bosses.sheets[k];
      this.footInset[k] = m.foot || 0; this.sideInset[k] = m.side || 0;   // 아직 안 읽은 보스 시트는 매니페스트 값
      this._pad(k, m);
    }
    return this;
  },
  _pad(k: string, m: any) {
    const im = this.img[k];
    if (!im || !im.width) return;
    try {
      const pad = this._measurePad(im, m);
      this.footInset[k] = pad.foot; this.sideInset[k] = pad.side;
    } catch (e) {
      /* ★ 못 잴 때는 0 이 아니라 **매니페스트에 적어 둔 값**을 쓴다. */
      this.footInset[k] = m.foot || 0; this.sideInset[k] = m.side || 0;
      this.tainted = 1;
    }
  },
  /** 늦게 읽는 시트(보스)를 지금 쓰겠다 — 없으면 읽기 시작한다. 그림이 준비됐으면 돌려준다. */
  want(key: string) {
    const im = this.img[key];
    if (this.lazy[key]) {
      this.used[key] = performance.now();
      if (!im && !this.loading[key] && !this.failed[key]) {
        this.loading[key] = 1;
        imageJob(this.base + this.lazy[key] + (this._ver ? '?' + this._ver : ''), got => {
          got.addEventListener('load', () => { delete this.loading[key]; this.img[key] = got; this._pad(key, this.meta.bosses.sheets[key]); this.used[key] = performance.now(); }, { once: true });
          got.addEventListener('error', () => { delete this.loading[key]; this.failed[key] = 1; }, { once: true });
        });
      }
    }
    return im && im.width ? im : null;
  },
  /** 2분 넘게 안 그린 늦은 시트와 그 사본(불 · 서리 · 색)을 놓는다 — 다시 필요하면 want() 가 또 읽는다. */
  gc(now = performance.now(), idle = 120000) {
    for (const k in this.lazy) {
      if (!this.img[k] || now - (this.used[k] || 0) < idle) continue;
      delete this.img[k];
      for (const dk in this.baseOf) if (this.baseOf[dk] === k) { delete this.img[dk]; delete this.meta.characters.sheets[dk]; delete this.baseOf[dk]; }
    }
  },
  /** 사본을 만들 원본 — 늦은 시트가 아직 없으면 null 을 **기억하지 않는다**(읽히면 다시 만든다). */
  _src(key: string, dk: string) {
    const im = this.lazy[key] ? this.want(key) : this.img[key];
    if (im && this.lazy[key]) this.baseOf[dk] = key;
    return im;
  },

  /** 시트 프레임 0의 알파 채널을 한 번 훑어 두 가지를 잰다(engine/assets/image.js). */
  _measurePad(im: any, m: any) { return measurePad(im, m.frameW, m.frameH, this.sc(m)); },
  /** 시트의 배율 — 키운 보스 · 자세 시트는 1(그림 한 칸 = 게임 1px), 나머지는 무리 기본 4 */
  sc(m: any) { return (m && m.scale) || this.scale; },


  /* ================= 개조 시트 ================= */
  mechSheet(key: string) {
    const have = this.img['mech_' + key];
    if (have !== undefined) return have;
    const im = this.img[key];
    const m = this.meta && (this.meta.characters.sheets[key] || this.meta.bosses.sheets[key]);
    if (!im || !im.width || !m) return (this.img['mech_' + key] = null);

    const cv = document.createElement('canvas');
    cv.width = im.naturalWidth || im.width; cv.height = im.naturalHeight || im.height;
    const g = cv.getContext('2d');
    g!.imageSmoothingEnabled = false;
    g!.drawImage(im, 0, 0);

    const S = this.sc(m), W = cv.width, H = cv.height;
    g!.globalCompositeOperation = 'source-atop';

    // 1.
    const grad = g!.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, '#7d8896');
    grad.addColorStop(0.5, '#4d555f');
    grad.addColorStop(1, '#333a43');
    g!.globalAlpha = 0.66;
    g!.fillStyle = grad; g!.fillRect(0, 0, W, H);

    /* 2. */
    g!.globalAlpha = 0.22; g!.fillStyle = '#171c23';
    for (let y = 7 * S; y < H; y += 13 * S) g!.fillRect(0, y, W, S);
    g!.globalAlpha = 0.55; g!.fillStyle = '#cfd8e2';
    for (let y = 5 * S; y < H; y += 13 * S)
      for (let x = 3 * S; x < W; x += 9 * S) g!.fillRect(x, y, S, S);

    /* 화로 불빛은 여기서 굽지 않는다. */
    g!.globalCompositeOperation = 'source-over'; g!.globalAlpha = 1;
    /* 매니페스트에도 같은 규격으로 등록해 둔다 — draw() 는 여기서 프레임 칸을 읽는다. */
    this.meta.characters.sheets['mech_' + key] = m;
    this.footInset['mech_' + key] = this.footInset[key] || 0;
    this.sideInset['mech_' + key] = this.sideInset[key] || 0;
    return (this.img['mech_' + key] = cv);
  },

  /** 얼어붙은 사본 — 그림 윤곽 안에만 서리를 입힌다(발에서 짙고 위로 옅게 + 흰 얼음 알갱이).
      판정 상자에 칠하면 몸이 네모 얼음에 갇혀 보였다. 키는 'frost_' + key(개조 몹은 frost_mech_*) */
  frostSheet(key: string) {
    const fk = 'frost_' + key, have = this.img[fk];
    if (have !== undefined) return have;
    const im = this._src(key, fk);
    const m = this.meta && (this.meta.characters.sheets[key] || this.meta.bosses.sheets[key]);
    if (!im || !im.width || !m) return this.lazy[key] ? null : (this.img[fk] = null);
    const cv = document.createElement('canvas');
    cv.width = im.naturalWidth || im.width; cv.height = im.naturalHeight || im.height;
    const g = cv.getContext('2d')!, S = this.sc(m), W = cv.width, H = cv.height, fh = m.frameH * S;
    g.imageSmoothingEnabled = false; g.drawImage(im, 0, 0);
    g.globalCompositeOperation = 'source-atop';
    for (let y0 = 0; y0 < H; y0 += fh) {                     // 프레임 줄마다 같은 결
      const grad = g.createLinearGradient(0, y0, 0, y0 + fh);
      grad.addColorStop(0, 'rgba(200,236,255,0.30)'); grad.addColorStop(0.55, 'rgba(170,222,255,0.50)'); grad.addColorStop(1, 'rgba(225,246,255,0.72)');
      g.fillStyle = grad; g.fillRect(0, y0, W, fh);
    }
    g.fillStyle = 'rgba(255,255,255,0.85)';
    for (let y = 0; y < H; y += S * 3) for (let x = (y / S) % 5 * S; x < W; x += S * 5) if (((x * 7 + y * 13) / S) % 11 < 3) g.fillRect(x, y, S, S);
    g.globalCompositeOperation = 'source-over';
    this.meta.characters.sheets[fk] = m;
    this.footInset[fk] = this.footInset[key] || 0;
    this.sideInset[fk] = this.sideInset[key] || 0;
    return (this.img[fk] = cv);
  },

  /** 스킬 연출 결 그림을 색 c 로 물들인 한 장(engine Vfx.art) — 흰 모양을 source-in 으로 칠하고 흰 속불을 얹는다.
      ★ 픽셀을 읽지 않는다(file:// 에서 PNG 캔버스가 더럽혀져 getImageData 가 막힌다). 색마다 한 번 굽고 들고 있는다 */
  vfxArt(name: string, c: string) {
    const ck = name + '|' + c, cache = this.vfxCache || (this.vfxCache = new Map());
    const have = cache.get(ck); if (have) return have;
    const sh = this.img['vfx_' + name], core = this.img['vfx_' + name + '_core'];
    if (!sh || !sh.width || !core || !core.width) return null;
    const cv = document.createElement('canvas'); cv.width = sh.width; cv.height = sh.height;
    const g = cv.getContext('2d')!;
    g.drawImage(sh, 0, 0); g.globalCompositeOperation = 'source-in'; g.fillStyle = c; g.fillRect(0, 0, cv.width, cv.height);
    g.globalCompositeOperation = 'source-over'; g.drawImage(core, 0, 0);
    if (cache.size > 160) cache.clear();
    cache.set(ck, cv); return cv;
  },

  /** 불붙은 사본 — 그림 윤곽 안을 아래에서 위로 달군다(발치는 이글대는 주황 · 위는 그을린 검붉음). 키는 'burn_' + key */
  burnSheet(key: string) {
    const bk = 'burn_' + key, have = this.img[bk];
    if (have !== undefined) return have;
    const im = this._src(key, bk);
    const m = this.meta && (this.meta.characters.sheets[key] || this.meta.bosses.sheets[key]);
    if (!im || !im.width || !m) return this.lazy[key] ? null : (this.img[bk] = null);
    const cv = document.createElement('canvas');
    cv.width = im.naturalWidth || im.width; cv.height = im.naturalHeight || im.height;
    const g = cv.getContext('2d')!, fh = m.frameH * this.sc(m);
    g.imageSmoothingEnabled = false; g.drawImage(im, 0, 0);
    g.globalCompositeOperation = 'source-atop';
    for (let y0 = 0; y0 < cv.height; y0 += fh) {
      const grad = g.createLinearGradient(0, y0, 0, y0 + fh);
      grad.addColorStop(0, 'rgba(40,10,6,0.45)'); grad.addColorStop(0.5, 'rgba(150,40,12,0.35)'); grad.addColorStop(1, 'rgba(255,140,40,0.55)');
      g.fillStyle = grad; g.fillRect(0, y0, cv.width, fh);
    }
    g.globalCompositeOperation = 'source-over';
    this.meta.characters.sheets[bk] = m;
    this.footInset[bk] = this.footInset[key] || 0;
    this.sideInset[bk] = this.sideInset[key] || 0;
    return (this.img[bk] = cv);
  },

  /** 한 색으로 칠한 실루엣 사본(피격 섬광 · 페이즈 금빛) — 그림 윤곽 안만 col 로. 키는 'tint' + col + '_' + key
      ★ 프레임 칸을 fillRect 로 덮으면 투명 여백까지 칠해져 몹 둘레에 회색 네모가 떴다 */
  tintSheet(key: string, col: string) {
    const tk = 'tint' + col + '_' + key, have = this.img[tk];
    if (have !== undefined) return have ? tk : null;
    const im = this._src(key, tk);
    const m = this.meta && (this.meta.characters.sheets[key] || this.meta.bosses.sheets[key]);
    if (!im || !im.width || !m) { if (!this.lazy[key]) this.img[tk] = null; return null; }
    const cv = document.createElement('canvas');
    cv.width = im.naturalWidth || im.width; cv.height = im.naturalHeight || im.height;
    const g = cv.getContext('2d')!;
    g.drawImage(im, 0, 0); g.globalCompositeOperation = 'source-atop'; g.fillStyle = col; g.fillRect(0, 0, cv.width, cv.height);
    this.meta.characters.sheets[tk] = m;
    this.footInset[tk] = this.footInset[key] || 0;
    this.sideInset[tk] = this.sideInset[key] || 0;
    this.img[tk] = cv;
    return tk;
  },

  /* 시트 한 프레임을 캔버스 좌표(x,y)에 게임 픽셀 크기로 그린다. */
  draw(c: CanvasRenderingContext2D, key: string, frame: number, x: number, y: number, flip: boolean) {
    const base = this.baseOf[key] || key;
    if (this.lazy[base]) {
      this.used[base] = performance.now();
      /* 늦은 시트가 읽히는 동안(한 번에 0.1~0.3초)은 그리지 않는다 — 거짓으로 돌리면 절차 그림이 번쩍였다. 못 읽으면 절차 그림으로 */
      if (!this.img[key]) { if (key === base) this.want(key); return !this.failed[base]; }
    }
    const im = this.img[key]; if (!im || !im.width) return false;
    const m = (this.meta.characters.sheets[key] || this.meta.bosses.sheets[key]);
    if (!m) return false;
    /* 시트가 제 gap을 들고 있으면 그것을 쓰고, 없을 때만 무리 기본값으로 떨어진다 — 사연: docs/code-history.md#h139 */
    const S = this.sc(m), cols = m.cols || 0;
    const isBoss = !!this.meta.bosses.sheets[key];
    const gap = m.gap !== undefined ? m.gap : (isBoss ? this.meta.bosses.gap : 0);
    let fw = m.frameW, fh = m.frameH, ox = m.ox || 0, oy = m.oy || 0;
    /* 시트를 다시 구워 프레임 크기가 바뀌었는데 브라우저가 옛 그림을 캐시에서 내주면 (매니페스트는 no-cache라 새것, 그림은 ?v= 그대로라 옛것) 칸이 어긋나 그림이 썰려 보인다. */
    if (!gap && im.naturalWidth && m.count &&
        Math.round(im.naturalWidth / S / (cols || m.count)) !== fw) {
      fw = Math.round(im.naturalWidth / S / (cols || m.count));
      fh = Math.round(im.naturalHeight / S);
      ox = oy = 0;
    }
    const sw = fw * S, sh = fh * S;
    // 칸 — 여러 줄 시트(cols · 보스 자세)는 줄을 바꿔 읽는다
    const sx = (cols ? frame % cols : frame) * (sw + gap), sy = cols ? Math.floor(frame / cols) * (sh + gap) : 0;
    c.save();
    c.imageSmoothingEnabled = false;
    if (flip) { c.translate(Math.round(x) + ox + fw, Math.round(y) + oy); c.scale(-1, 1); }
    else c.translate(Math.round(x) + ox, Math.round(y) + oy);
    c.drawImage(im, sx, sy, sw, sh, 0, 0, fw, fh);
    c.restore();
    return true;
  },

  /* 이펙트 시트(투사체 16×16 / 폭발 64×64) 한 프레임을 size 크기로 그린다. */
  drawFx(c: CanvasRenderingContext2D, key: string, frame: number, x: number, y: number, size: any) {
    const im = this.img[key]; if (!im || !im.width || !this.meta) return false;
    const m = key.startsWith('proj_') ? this.meta.fx.projectiles
            : key.startsWith('smoke_') ? this.meta.fx.smoke
            : this.meta.fx.bursts;
    if (!m) return false;
    if (frame < 0 || frame >= m.count) return false;
    const S = m.scale, sw = m.frameW * S, sh = m.frameH * S;
    const sx = frame * (sw + m.gap);
    const prev = c.imageSmoothingEnabled;
    c.imageSmoothingEnabled = false;
    c.drawImage(im, sx, 0, sw, sh, x, y, size, size);
    c.imageSmoothingEnabled = prev;
    return true;
  },

  /* 시설물을 게임 좌표(x,y)에 w×h 크기로 그린다. t = 게임 시각(두 장짜리 그림을 넘긴다) */
  drawObj(c: CanvasRenderingContext2D, key: string, x: number, y: number, w: number, h: number, t = 0) {
    const im = this.img[key]; if (!im || !im.width) return false;
    const want = w / h;
    const frames = Math.abs(im.width / im.height - want) < 0.03 ? 1
      : Math.abs(im.width / 2 / im.height - want) < 0.03 ? 2 : 1;
    const fw = im.width / frames;
    const fr = frames > 1 ? (((t * 3.5) | 0) % frames) : 0;
    c.save();
    c.imageSmoothingEnabled = false;
    c.drawImage(im, fr * fw, 0, fw, im.height, Math.round(x), Math.round(y), w, h);
    c.restore();
    return true;
  },

  /* 적 상태 → 프레임 인덱스 (기본 7프레임 규격: idle1 idle2 move1 move2 atk death1 death2) */
  enemyFrame(e: any, t: any) {
    if (e.dying) return e.dying > .12 ? 5 : 6;
    if (e.attacking) return 4;
    if (Math.abs(e.vx) > 6) return 2 + (Math.floor(t * 7) % 2);
    return Math.floor(t * 2.4) % 2;
  },

  /* 플레이어 상태 → 프레임 인덱스 (13프레임) */
  playerFrame(p: any, t: any) {
    if (p.iframe > 0 && p.hurtT > 0) return 12;
    if (p.dashT > 0) return 8;
    if (p.swing > 0) return 9 + Math.min(2, Math.floor((0.24 - p.swing) / 0.08));
    if (!p.onGround) return p.vy < 0 ? 6 : 7;
    if (Math.abs(p.vx) > 20) return 2 + (Math.floor(t * 9) % 4);
    return Math.floor(t * 2) % 2;
  }
};
